import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { GlobalService } from '@core/services/global/global.service';
import { Subject, takeUntil } from 'rxjs';
import { GroupRosterService } from '@core/services/GroupRoster/group-roster.service';
import { GroupRoaster, Organization } from '@interface/groupRoaster';
import { jsPDF } from 'jspdf'; // Import the jsPDF type
import { NadaSpinnerComponent } from '@shared/components/nada-spinner/nada-spinner.component';
import { ErrorPageComponent } from '@shared/components/app-error-page/app-error-page.component';
import { GroupMember } from '../../Interface/Dealer';
import { BaseErrorComponent } from '../baseerror/baseerror.component';

@Component({
  selector: 'app-group-roster',
  standalone: true,
  imports: [NadaSpinnerComponent, ErrorPageComponent],
  templateUrl: './group-roster.component.html',
  styleUrl: './group-roster.component.css',
})
export class GroupRosterComponent
  extends BaseErrorComponent
  implements OnInit, OnDestroy
{
  @ViewChild('headerdata') headerdata!: ElementRef;
  @ViewChild('tblData') tblData!: ElementRef;

  private readonly unsubscribe$ = new Subject<void>();
  selectedDealer: GroupMember | null = null;
  organization?: Organization;
  isSpinner = false;
  title = 'Group Roster';
  groupRoasterData: GroupRoaster[] = [];
  name = 'NATIONAL  AUTOMOBILE  DEALERS  ASSOCIATION';
  address = '8484 Westpark Drive, Suite 500, Tysons, VA 22102';
  currentDate?: string;

  constructor(
    private globalService: GlobalService,
    private readonly groupRosterService: GroupRosterService,
  ) {
    super();
  }

  get GroupName() {
    return this.selectedDealer?.groupMemberCode.slice(0, -2);
  }

  ngOnInit() {
    this.subscribeToOrganizationData();
    this.subscribeToGroupRoasterData();
    this.globalService.selectedDealer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async (name) => {
        this.selectedDealer = name;
        this.getDate();
        if (this.selectedDealer?.groupMemberCode) {
          await Promise.all([
            await this.getOrganizationData(),
            await this.getGroupRoasterData(),
          ]);
        }
      });
  }

  private getDate(): void {
    const now = new Date();
    this.currentDate = now.toLocaleDateString('en-US').replace(/\//g, '-');
  }

  private subscribeToGroupRoasterData(): void {
    this.isSpinner = true;
    this.groupRosterService.groupRoasterData$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        this.isSpinner = false;
        if (state != undefined) {
          // If state.data is an array, assign it directly; otherwise, wrap it in an array
          this.groupRoasterData = Array.isArray(state?.data)
            ? state.data
            : [state?.data].filter(Boolean);
        } else {
          this.groupRoasterData = [];
        }
      });
  }

  private subscribeToOrganizationData(): void {
    this.groupRosterService.organizationDetails$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        if (state) this.organization = state;
      });
  }

  private async getGroupRoasterData(): Promise<void> {
    this.resetErrorPage();
    const response = await this.groupRosterService.fetchGroupRosterData();
    if (response.statusCode !== 200) {
      this.setErrorPage(response.statusCode);
      return;
    }
    if (!response.data.data.length) {
      this.setErrorPage(204);
    }
  }

  private async getOrganizationData(): Promise<void> {
    await this.groupRosterService.getOrganizationData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.complete();
  }

  printGroupRoaster() {
    if (typeof window !== 'undefined') {
      const element = this.tblData.nativeElement; // The section you want to print
      const headerelement = this.headerdata.nativeElement; // The section you want to print

      if (headerelement && element) {
        this.isSpinner = true;
        // Create a temporary div to merge content

        const combinedDiv = document.createElement('div');
        headerelement.classList.add('print-mode');
        element.classList.add('print-mode');

        // Append header content
        combinedDiv.appendChild(headerelement.cloneNode(true));

        // Append table/content
        combinedDiv.appendChild(element.cloneNode(true));

        // Force all hidden/scrolled content to render
        element.style.height = 'auto';
        element.style.overflow = 'visible';

        // Dynamically import html2pdf.js only in the browser environment
        import('html2pdf.js')
          .then((html2pdf: any) => {
            const options = {
              margin: 5,
              filename: 'GroupRoaster.pdf',
              image: { type: 'jpeg', quality: 1 },
              html2canvas: {
                scale: 3, // High scale for better resolution
                useCORS: true,
                allowTaint: true,
                scrollX: 0, // Ensure horizontal scrolling is captured
                scrollY: 0, // Ensure vertical scrolling is captured
                windowWidth: document.body.scrollWidth,
                windowHeight: document.body.scrollHeight,
              },
              jsPDF: { unit: 'mm', format: 'a4' },
              pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
            };
            html2pdf
              .default()
              .from(combinedDiv)
              .set(options)
              .toPdf()
              .get('pdf')
              .then((htmlPdf: jsPDF) => {
                htmlPdf.autoPrint();
                window.open(htmlPdf.output('bloburl'), '_blank');
              })
              .finally(() => {
                headerelement.classList.remove('print-mode');
                element.classList.remove('print-mode');
                this.isSpinner = false;
              });
          })
          .catch((error) => {
            headerelement.classList.remove('print-mode');
            element.classList.remove('print-mode');
            this.isSpinner = false;
            console.error('Failed to load html2pdf.js', error);
          });
      }
    }
  }
}
